"use client";
import { Suspense, useEffect, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import { ArrowRight, FileText, Plus, RefreshCw, Search } from "lucide-react";
import { RequireRole } from "../console-shell";
import { Banner, Button, Desk, PageHeader, StatusTag } from ".";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { ReadState, useRead, type PageResult } from "./record-tools";
import { ParticipationEditor } from "./participation-editor";
import { CallReading, ClueReading, TopicReading } from "./participation-detail";
import {
  CALL_LABEL,
  CALL_TONE,
  CLUE_LABEL,
  CLUE_TONE,
  TOPIC_LABEL,
  TOPIC_TONE,
  ENDPOINT,
  TITLES,
  CLUE_KIND_LABEL,
  type CallSummary,
  type ClueSummary,
  type TopicSummary,
  type CallDetail,
  type ClueDetail,
  type TopicDetail,
  type Summary,
  type Detail,
  type ParticipationKind,
  type Editor
} from "./participation-model";
import { formatTime } from "../../lib/api";

export function ParticipationWorkspace({ kind }: { kind: ParticipationKind }) {
  return (
    <RequireRole roleCode="PARTICIPATION_REVIEWER">
      <Suspense fallback={<Desk>正在读取工作台…</Desk>}>
        <ParticipationDesk key={kind} kind={kind} />
      </Suspense>
    </RequireRole>
  );
}
function ParticipationDesk({ kind }: { kind: ParticipationKind }) {
  const params = useSearchParams();
  const [status, setStatus] = useState(
      params.get("task")
        ? ""
        : kind === "clue"
          ? "SUBMITTED"
          : kind === "call"
            ? "PUBLISHED"
            : "ACTIVE"
    ),
    [word, setWord] = useState(""),
    [q, setQ] = useState("");
  const [task, setTask] = useState(params.get("task") ?? ""),
    [year, setYear] = useState(""),
    [yearDraft, setYearDraft] = useState("");
  const [cursors, setCursors] = useState([""]),
    [index, setIndex] = useState(0),
    [selected, setSelected] = useState<string | null>(params.get("record"));
  const [editor, setEditor] = useState<Editor | null>(null),
    [message, setMessage] = useState("");
  const [sourceId, setSourceId] = useState(kind === "topic" ? params.get("newFrom") : null);
  const [readAt, setReadAt] = useState("");
  const focus = useRef<HTMLElement | null>(null),
    heading = useRef<HTMLHeadingElement>(null);
  const request = new URLSearchParams({
    limit: "20",
    ...(status ? { status } : {}),
    ...(q ? { q } : {}),
    ...(task && kind === "clue" ? { taskId: task } : {}),
    ...(year && kind === "topic" ? { year } : {}),
    ...(cursors[index] ? { cursor: cursors[index] } : {})
  });
  const list = useRead<PageResult<Summary>>(`${ENDPOINT[kind]}?${request}`);
  const detail = useRead<Detail>(
    selected ? `${ENDPOINT[kind]}/${encodeURIComponent(selected)}` : null
  );
  const source = useRead<ClueDetail>(
    sourceId ? `${ENDPOINT.clue}/${encodeURIComponent(sourceId)}` : null
  );
  const call = useRead<CallDetail>(
    task && kind === "clue" ? `${ENDPOINT.call}/${encodeURIComponent(task)}` : null
  );
  const openedSource = useRef<string | null>(null);
  useEffect(() => {
    if (
      source.data?.status === "ACCEPTED_FOR_FOLLOW_UP" &&
      openedSource.current !== source.data.id
    ) {
      openedSource.current = source.data.id;
      setEditor({ kind: "topic", action: "create", record: null, source: source.data });
    }
  }, [source.data]);
  useEffect(() => {
    if (list.data) setReadAt(new Date().toISOString());
  }, [list.data]);
  useEffect(() => {
    const id = params.get("record");
    if (id) setSelected(id);
  }, [params]);
  const reset = () => {
    setCursors([""]);
    setIndex(0);
  };
  const open = (id: string) => {
    focus.current = document.activeElement as HTMLElement;
    setSelected(id);
  };
  const labels: Record<string, string> =
    kind === "clue" ? CLUE_LABEL : kind === "call" ? CALL_LABEL : TOPIC_LABEL;
  const tones: Record<string, "neutral" | "brand" | "success" | "warning"> =
    kind === "clue" ? CLUE_TONE : kind === "call" ? CALL_TONE : TOPIC_TONE;
  const makeNew = () =>
    setEditor(
      kind === "call"
        ? { kind: "call", action: "create", record: null }
        : { kind: "topic", action: "create", record: null }
    );
  if (editor)
    return (
      <Desk>
        <ParticipationEditor
          editor={editor}
          onCancel={() => {
            setEditor(null);
            setSourceId(null);
          }}
          onSaved={(result) => {
            const saved = result as Detail;
            setEditor(null);
            setSourceId(null);
            setMessage(
              editor.kind === "call" && editor.action === "create"
                ? `征集已发起，面向 ${(result as CallDetail).audienceCount} 名会员。`
                : editor.kind === "topic" && editor.action === "submission"
                  ? "待报送记录已生成，请在实际报送后登记执行情况。"
                  : "办理已完成，正在读取最新记录。"
            );
            setSelected(saved.id);
            void list.reload();
            if (selected === saved.id) void detail.reload();
          }}
        />
      </Desk>
    );
  return (
    <Desk wide>
      <PageHeader
        eyebrow="参政议政 / 材料与办理"
        title={TITLES[kind]}
        description={
          kind === "clue"
            ? "从待受理的建言开始，核对材料并向会员说明下一步。"
            : kind === "call"
              ? "围绕具体问题征集材料，让每一份提交都有明确去向。"
              : "从来源线索到稿件版本，逐步记录调研、报送与外部反馈。"
        }
        actions={
          kind !== "clue" ? (
            <Button onClick={makeNew}>
              <Plus size={16} />
              {kind === "call" ? "发起征集" : "提出课题"}
            </Button>
          ) : undefined
        }
      />
      <nav className="participation-nav" aria-label="参政议政工作区">
        {(
          [
            ["clue", "clues"],
            ["call", "calls"],
            ["topic", "topics"]
          ] as const
        ).map(([id, path]) => (
          <Link key={id} href={`/${path}`} aria-current={id === kind ? "page" : undefined}>
            {TITLES[id]}
          </Link>
        ))}
      </nav>
      {message && <Banner tone="success">{message}</Banner>}
      {sourceId && (
        <>
          <ReadState {...source} retry={source.reload} />
          {source.data && source.data.status !== "ACCEPTED_FOR_FOLLOW_UP" && (
            <Banner tone="warning">这条建言尚未进入后续跟进，请先在建言受理中核对状态。</Banner>
          )}
        </>
      )}
      {task && (
        <div className="participation-context">
          <span>
            所属征集：{call.data?.title || (call.error ? "征集信息未读取" : "正在读取征集")}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setTask("");
              reset();
            }}
          >
            取消限定
          </Button>
          <ReadState {...call} retry={call.reload} />
        </div>
      )}
      <div className="participation-filters" aria-label="状态筛选">
        <button
          aria-pressed={!status}
          onClick={() => {
            setStatus("");
            reset();
          }}
        >
          全部
        </button>
        {Object.entries(labels).map(([value, label]) => (
          <button
            key={value}
            aria-pressed={status === value}
            onClick={() => {
              setStatus(value);
              reset();
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="record-toolbar">
        <form
          className="record-search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(word.trim());
            setYear(yearDraft);
            reset();
          }}
        >
          <Search size={17} />
          <input
            aria-label="搜索标题或编号"
            value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder={kind === "call" ? "搜索征集标题" : "搜索标题或编号"}
          />
          {kind === "topic" && (
            <label className="participation-year">
              年度
              <input
                aria-label="筛选年度"
                type="number"
                min={2000}
                max={2200}
                placeholder="全部年度"
                value={yearDraft}
                onChange={(e) => setYearDraft(e.target.value)}
              />
            </label>
          )}
          <Button type="submit" variant="outline">
            搜索
          </Button>
        </form>
        <Button variant="ghost" aria-label="刷新列表" onClick={list.reload}>
          <RefreshCw size={16} />
        </Button>
      </div>
      <ReadState {...list} retry={list.reload} />
      {!list.loading && !list.error && (
        <>
          <div className="record-list-meta">
            <span>
              当前页 {list.data?.items.length ?? 0} 条 · {status ? labels[status] : "全部状态"}
            </span>
            <span>{readAt ? `${formatTime(readAt)} 读取` : ""}</span>
          </div>
          {list.data?.items.length ? (
            <div className="record-table-scroll">
              <table className="record-table participation-table">
                <caption className="sr-only">{TITLES[kind]}列表</caption>
                <thead>
                  <tr>
                    <th>
                      {kind === "clue"
                        ? "建言 / 收件编号"
                        : kind === "call"
                          ? "征集 / 材料体裁"
                          : "课题 / 年度方向"}
                    </th>
                    <th>{kind === "clue" ? "提交会员 / 组织" : "承办组织"}</th>
                    <th>当前状态</th>
                    <th>
                      {kind === "clue"
                        ? "提交时间"
                        : kind === "call"
                          ? "已交材料 / 截止时间"
                          : "稿件 / 报送"}
                    </th>
                    <th>
                      <span className="sr-only">操作</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <button className="participation-record" onClick={() => open(row.id)}>
                          <FileText size={18} aria-hidden="true" />
                          <span>
                            <strong>{row.title}</strong>
                            <small>
                              {kind === "clue"
                                ? `${CLUE_KIND_LABEL[(row as ClueSummary).kind ?? "ISSUE_CLUE"]} · ${(row as ClueSummary).publicRef}`
                                : kind === "call"
                                  ? (row as CallSummary).category
                                  : `${(row as TopicSummary).publicRef} · ${(row as TopicSummary).year} 年 · ${(row as TopicSummary).direction}`}
                            </small>
                          </span>
                        </button>
                      </td>
                      <td>
                        {kind === "clue" && (
                          <strong className="participation-member">
                            {(row as ClueSummary).member.displayName}
                          </strong>
                        )}
                        {row.organization.name}
                      </td>
                      <td>
                        <StatusTag tone={tones[row.status]}>{labels[row.status]}</StatusTag>
                        {kind === "call" && row.status === "PUBLISHED" && (
                          <small>
                            {Date.parse((row as CallSummary).opensAt) > Date.now()
                              ? "尚未开始"
                              : (row as CallSummary).closesAt &&
                                  Date.parse((row as CallSummary).closesAt!) <= Date.now()
                                ? "收件时间已结束"
                                : "正在收件"}
                          </small>
                        )}
                      </td>
                      <td>
                        {kind === "clue" ? (
                          formatTime((row as ClueSummary).submittedAt)
                        ) : kind === "call" ? (
                          <>
                            {(row as CallSummary).responseCount} 份
                            <small>
                              {(row as CallSummary).closesAt
                                ? formatTime((row as CallSummary).closesAt!)
                                : "长期征集"}
                            </small>
                          </>
                        ) : (
                          <>
                            {(row as TopicSummary).manuscriptCount} 个版本
                            <small>
                              {(row as TopicSummary).submissionCount} 条报送 ·{" "}
                              {(row as TopicSummary).outcomeCount} 条外部反馈
                            </small>
                          </>
                        )}
                      </td>
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label={`查看${row.title}`}
                          onClick={() => open(row.id)}
                        >
                          <ArrowRight size={15} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="workspace-empty">
              <FileText />
              <strong>没有匹配的记录</strong>
              <p>调整状态或关键词后再试。</p>
              <Button
                variant="outline"
                onClick={() => {
                  setStatus("");
                  setQ("");
                  setWord("");
                  setYear("");
                  setYearDraft("");
                  reset();
                }}
              >
                清除筛选
              </Button>
            </div>
          )}
          <div className="record-pagination">
            <span>第 {index + 1} 页</span>
            <Button variant="outline" disabled={index === 0} onClick={() => setIndex(index - 1)}>
              上一页
            </Button>
            <Button
              variant="outline"
              disabled={!list.data?.meta?.nextCursor}
              onClick={() => {
                setCursors((old) => [...old.slice(0, index + 1), list.data!.meta!.nextCursor!]);
                setIndex(index + 1);
              }}
            >
              下一页
            </Button>
          </div>
        </>
      )}
      <h2 className="sr-only" ref={heading} tabIndex={-1}>
        {TITLES[kind]}工作区
      </h2>
      <Sheet
        open={!!selected}
        onOpenChange={(value) => {
          if (!value) setSelected(null);
        }}
      >
        <SheetContent
          className="record-sheet participation-sheet workspace"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            (focus.current?.isConnected ? focus.current : heading.current)?.focus();
          }}
        >
          <SheetHeader>
            <p className="record-eyebrow">
              {detail.data && "publicRef" in detail.data ? detail.data.publicRef : TITLES[kind]}
            </p>
            <SheetTitle>{detail.data?.title || "读取详情"}</SheetTitle>
            <SheetDescription>
              {detail.data
                ? `${detail.data.organization.name} · ${labels[detail.data.status]}`
                : "按当前授权范围读取材料与办理记录。"}
            </SheetDescription>
          </SheetHeader>
          <ReadState {...detail} retry={detail.reload} />
          {detail.data &&
            (kind === "clue" ? (
              <ClueReading
                key={detail.data.id}
                record={detail.data as ClueDetail}
                onAction={setEditor}
              />
            ) : kind === "call" ? (
              <CallReading
                key={detail.data.id}
                record={detail.data as CallDetail}
                onAction={setEditor}
              />
            ) : (
              <TopicReading
                key={detail.data.id}
                record={detail.data as TopicDetail}
                onAction={setEditor}
              />
            ))}
        </SheetContent>
      </Sheet>
    </Desk>
  );
}
